import React, { useState } from 'react'
import { Button, makeStyles, Typography } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import HelpRounded from '@material-ui/icons/HelpRounded'
import RiskEventTooltipModal from './RiskEventTooltipModal'
import FundamentalTooltipModal from './FundamentalTooltipModal'
import BehaviouralTooltipModal from './BehaviouralTooltipModal'
import FundamentalCreditTooltipModal from './FundamentalCreditTooltipModal'
import { getTrendSymbol } from '../ultils/chart-d3-utils'

const useStyles = makeStyles({
  section: {
    padding: '26px 20px 20px 30px',
    marginBottom: '20px',
    background: 'white',
    border: '1px solid #DFE4EB',
    borderRadius: 5,
    boxSizing: 'border-box',
  },
  title: {
    lineHeight: '24px',
    fontWeight: 400,
    color: '#181818',
    marginRight: 5,
  },
  leftSection: {
    display: 'flex',
  },
  leftMobileSection: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    alignItems: 'flexStart',
  },
  toolTip: {
    color: '#8c8c8c',
    margin: 'auto',
    fontSize: '1.8rem',
    cursor: 'pointer',
    marginLeft: '5px',
    marginTop: -3,
    height: '30px',
  },
  impliedRating: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: 50,
    fontSize: 20,
  },
  subTitle: {
    fontSize: 'calc(65% + 0.2vw)',
    marginTop: 3,
  },
  subTitleMobile: {
    fontSize: 10,
    marginTop: 6,
    fontWeight: 600,
    color: '#474747',
  },
  head: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #DFE4EB',
  },
  mobileHead: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #DFE4EB',
  },
  statusMobile: {
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
    '& div': {
      fontWeight: 600,
      fontSize: '16px !important',
    },
  },
  status: {
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '10px !important',
    '& div': {
      fontWeight: 600,
      fontSize: '16px !important',
      marginLeft: 10,
    },
  },
  circle: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    position: 'relative',
    '& > div': {
      height: 16,
      width: 16,
      opacity: '0.35',
      borderRadius: '50%',
      position: 'absolute',
      left: '-4px',
      top: '-4px',
      margin: 0,
      'animation-name': `$backgroundColorPalette`,
      'animation-duration': '1s',
      'animation-iteration-count': 'infinite',
      'animation-direction': 'alternate',
      'animation-timing-function': 'liner',
    },
  },
  '@keyframes backgroundColorPalette': {
    '0%': {
      opacity: 0.25,
    },
    '25%': {
      opacity: 0.45,
    },
    '50%': {
      opacity: 0.55,
    },
    '75%': {
      opacity: 0.45,
    },
    '100%': {
      opacity: 0.25,
    },
  },
  button: {
    cursor: 'pointer',
    verticalAlign: 'text-top',
    marginLeft: 5,
    height: '20px',
    color: '#fff',
    backgroundColor: '#1B3963',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    fontWeight: 600,
    fontSize: 8,
    padding: '2px 3px',
    '&:hover': {
      backgroundColor: '#1B3963',
      color: '#fff',
    },
  },
})

interface PageSectionProps {
  title: string
  subTitle?: string
  children: any
  customStyle: any
  riskEventToolTip: boolean
  fundamentalTooltip: boolean
  behaviouralToolTip: boolean
  fundamentalCreditTooltip: boolean
  companyInfo?: any
  showImpliedRating?: boolean
  isShowingRisk?: boolean
  riskStatus?: any
}

const PageSection = ({
  title,
  subTitle,
  children,
  customStyle,
  riskEventToolTip,
  fundamentalTooltip,
  fundamentalCreditTooltip,
  behaviouralToolTip,
  companyInfo,
  showImpliedRating,
  isShowingRisk,
  riskStatus,
}: PageSectionProps) => {
  const classes = useStyles()
  const [openTooltip, setOpenTooltip] = React.useState(false)
  const handleClickOpenTooltip = () => {
    setOpenTooltip(true)
  }

  return (
    <>
      <section
        className={classes.section}
        style={
          isMobile
            ? { ...customStyle, padding: '20px 16px' }
            : { ...customStyle, minWidth: '1300px' }
        }
      >
        <div
          className={isMobile ? classes.mobileHead : classes.head}
          style={{ paddingBottom: isMobile ? '16px' : '26px' }}
        >
          {!isMobile && (
            <>
              <div className={classes.leftSection}>
                <Typography
                  className={classes.title}
                  style={{ fontSize: isMobile ? 18 : 20 }}
                >
                  {title}
                </Typography>
                {subTitle && (
                  <span className={classes.subTitle}>({subTitle})</span>
                )}
                {riskEventToolTip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {fundamentalTooltip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {fundamentalCreditTooltip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  >
                    Quick Guide
                  </HelpRounded>
                )}
                {behaviouralToolTip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {showImpliedRating && companyInfo && companyInfo.bfaiRating && (
                  <div
                    className={classes.impliedRating}
                    // the id indicates this element is ignored when generating pdf
                    id="impliedRatingInfo"
                  >
                    <div className={classes.title} style={{ marginRight: 0 }}>
                      Implied Rating{' '}
                      <span style={{ fontWeight: 600 }}>
                        {companyInfo.bfaiRating}
                      </span>
                    </div>
                    <div className={classes.title}>
                      <span
                        style={{
                          fontSize: 28,
                        }}
                      >
                        {getTrendSymbol(companyInfo.rating_trend)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {isShowingRisk && (
                <div className={classes.status}>
                  <div
                    className={classes.circle}
                    style={{
                      backgroundColor: riskStatus.color,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: riskStatus.color,
                      }}
                    >
                      {' '}
                    </div>
                  </div>
                  <div
                    style={{
                      color: riskStatus.color,
                    }}
                    className="statusName"
                  >
                    {riskStatus.text}
                  </div>
                </div>
              )}
            </>
          )}

          {isMobile && (
            <div className={classes.leftMobileSection}>
              <Typography className={classes.title}>
                {title}
                <p className={classes.subTitleMobile}>({subTitle})</p>
                {riskEventToolTip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {fundamentalTooltip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {fundamentalCreditTooltip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
                {behaviouralToolTip && (
                  <HelpRounded
                    className={classes.toolTip}
                    key="1"
                    onClick={() => handleClickOpenTooltip()}
                  />
                )}
              </Typography>
            </div>
          )}
          {!isMobile && children[0]}
          {isMobile && children[0]}
          {isMobile && isShowingRisk && (
            <div className={classes.statusMobile}>
              <div
                style={{
                  color: riskStatus.color,
                  marginRight: 10,
                }}
                className="statusName"
              >
                {riskStatus.text}
              </div>
              <div
                className={classes.circle}
                style={{
                  backgroundColor: riskStatus.color,
                }}
              >
                <div
                  style={{
                    backgroundColor: riskStatus.color,
                  }}
                >
                  {' '}
                </div>
              </div>
            </div>
          )}
        </div>
        {children[1]}
      </section>
      {riskEventToolTip && (
        <RiskEventTooltipModal
          open={openTooltip}
          handleClose={() => {
            setOpenTooltip(false)
          }}
        />
      )}
      {fundamentalTooltip && (
        <FundamentalTooltipModal
          open={openTooltip}
          handleClose={() => {
            setOpenTooltip(false)
          }}
        />
      )}
      {fundamentalCreditTooltip && (
        <FundamentalCreditTooltipModal
          open={openTooltip}
          handleClose={() => {
            setOpenTooltip(false)
          }}
        />
      )}
      {behaviouralToolTip && (
        <BehaviouralTooltipModal
          open={openTooltip}
          handleClose={() => {
            setOpenTooltip(false)
          }}
        />
      )}
    </>
  )
}

export default PageSection
